/* ============================================================
   Gloam global JS — progressive enhancement only.
   Header state, drawers (menu / search / cart), cart mutations
   via the AJAX Cart API + Section Rendering API, predictive
   search, and scroll reveals. No dependencies.
   ============================================================ */
(function () {
  'use strict';

  var routes = window.gloamRoutes || {};
  var strings = window.gloamStrings || {};
  var liveRegion = document.getElementById('GloamLiveRegion');

  function announce(message) {
    if (!liveRegion || !message) return;
    liveRegion.textContent = '';
    window.requestAnimationFrame(function () { liveRegion.textContent = message; });
  }

  /* ---------- Header: transparent → solid on scroll ---------- */
  var header = document.querySelector('[data-gloam-header]');
  if (header) {
    var solidAlways = header.dataset.transparent !== 'true';
    var setHeaderState = function () {
      if (solidAlways || window.scrollY > 24) header.classList.add('is-solid');
      else header.classList.remove('is-solid');
    };
    setHeaderState();
    window.addEventListener('scroll', setHeaderState, { passive: true });
  }

  /* ---------- Generic drawer manager with focus trap ---------- */
  var openDrawer = null;
  var lastTrigger = null;

  function trapFocus(container, event) {
    var focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function show(drawer, trigger) {
    if (openDrawer && openDrawer !== drawer) hide(openDrawer);
    openDrawer = drawer;
    lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    var focusTarget = drawer.querySelector('[data-autofocus]') || drawer.querySelector('button, a[href], input');
    if (focusTarget) focusTarget.focus();
  }

  function hide(drawer) {
    drawer.hidden = true;
    document.body.style.overflow = '';
    if (openDrawer === drawer) openDrawer = null;
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) {
        event.preventDefault();
        show(target, opener);
      }
      return;
    }
    var closer = event.target.closest('[data-drawer-close]');
    if (closer && openDrawer) {
      event.preventDefault();
      hide(openDrawer);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!openDrawer) return;
    if (event.key === 'Escape') hide(openDrawer);
    if (event.key === 'Tab') trapFocus(openDrawer, event);
  });

  /* ---------- Cart ---------- */
  function refreshCartDrawer(openAfter) {
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (response) { return response.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer]');
        var current = document.querySelector('[data-cart-drawer]');
        if (fresh && current) {
          var wasHidden = current.hidden;
          current.innerHTML = fresh.innerHTML;
          current.hidden = openAfter ? false : wasHidden;
          if (openAfter) show(current);
        }
      });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  }

  function cartError(message) {
    var el = document.querySelector('[data-cart-error]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    announce(message);
  }

  // Add-to-cart: intercept product forms
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-product-form]');
    if (!form) return;
    event.preventDefault();

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
    }
    announce(strings.loading || 'Loading');

    fetch(routes.cartAdd + '.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.json().then(function (data) { return { ok: response.ok, data: data }; }); })
      .then(function (result) {
        if (!result.ok) {
          cartError(result.data.description || strings.cartError || 'Error');
          return;
        }
        announce(strings.itemAdded || 'Item added');
        return fetch(routes.cart + '.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            var drawer = document.querySelector('[data-cart-drawer]');
            if (drawer) return refreshCartDrawer(true);
            window.location.href = routes.cart;
          });
      })
      .catch(function () { cartError(strings.cartError || 'Error'); })
      .finally(function () {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('is-loading');
        }
      });
  });

  // Quantity / remove inside cart drawer and cart page
  function changeLine(line, quantity, lineEl) {
    if (lineEl) lineEl.classList.add('is-updating');
    fetch(routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (response) { return response.json().then(function (data) { return { ok: response.ok, data: data }; }); })
      .then(function (result) {
        if (!result.ok) {
          cartError(result.data.description || strings.cartError || 'Error');
          if (lineEl) lineEl.classList.remove('is-updating');
          return;
        }
        updateCartCount(result.data.item_count);
        announce(strings.cartUpdated || 'Cart updated');
        if (document.querySelector('[data-cart-page]')) {
          window.location.reload();
        } else {
          refreshCartDrawer(false);
        }
      })
      .catch(function () {
        cartError(strings.cartError || 'Error');
        if (lineEl) lineEl.classList.remove('is-updating');
      });
  }

  document.addEventListener('click', function (event) {
    var stepBtn = event.target.closest('[data-qty-change]');
    if (stepBtn && stepBtn.closest('[data-cart-drawer], [data-cart-page]')) {
      var container = stepBtn.closest('.gloam-qty');
      var input = container && container.querySelector('[data-qty-input]');
      if (!input) return;
      var next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(stepBtn.dataset.qtyChange, 10));
      changeLine(parseInt(stepBtn.dataset.line, 10), next, stepBtn.closest('[data-line]'));
      return;
    }
    var removeBtn = event.target.closest('[data-line-remove]');
    if (removeBtn) {
      changeLine(parseInt(removeBtn.dataset.line, 10), 0, removeBtn.closest('[data-line]'));
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-qty-input]');
    if (input && input.closest('[data-cart-drawer], [data-cart-page]')) {
      var qty = Math.max(0, parseInt(input.value || '0', 10));
      changeLine(parseInt(input.dataset.line, 10), qty, input.closest('[data-line]'));
    }
    var note = event.target.closest('[data-cart-note]');
    if (note) {
      fetch(routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }
  });

  /* ---------- Predictive search ---------- */
  var searchInput = document.querySelector('[data-predictive-input]');
  var searchResults = document.querySelector('[data-predictive-results]');
  var searchTimer = null;

  if (searchInput && searchResults && routes.predictiveSearch) {
    searchInput.setAttribute('role', 'combobox');
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.setAttribute('aria-controls', searchResults.id);

    searchInput.addEventListener('input', function () {
      var term = searchInput.value.trim();
      window.clearTimeout(searchTimer);
      if (term.length < 2) {
        searchResults.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
        return;
      }
      searchTimer = window.setTimeout(function () {
        var url = routes.predictiveSearch +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var inner = parsed.querySelector('#shopify-section-predictive-search');
            if (inner) {
              searchResults.innerHTML = inner.innerHTML;
              searchResults.hidden = false;
              searchInput.setAttribute('aria-expanded', 'true');
            }
          });
      }, 250);
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-predictive-wrap]')) {
        searchResults.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ---------- Scroll reveals (respects reduced motion) ---------- */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionOff = document.body.classList.contains('motion-off');
  var revealTargets = document.querySelectorAll('[data-reveal]');

  if (prefersReduced || motionOff || !('IntersectionObserver' in window)) {
    revealTargets.forEach(function (el) { el.classList.add('is-visible'); });
  } else if (revealTargets.length) {
    var pending = Array.prototype.slice.call(revealTargets);
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    pending.forEach(function (el) { observer.observe(el); });

    // Fast or programmatic scrolling can jump past elements between frames,
    // so the observer never fires for them. Sweep anything at or above the
    // viewport on every scroll as a safety net.
    var sweep = function () {
      pending = pending.filter(function (el) {
        if (el.classList.contains('is-visible')) return false;
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-visible');
          observer.unobserve(el);
          return false;
        }
        return true;
      });
      if (!pending.length) window.removeEventListener('scroll', sweep);
    };
    window.addEventListener('scroll', sweep, { passive: true });
  }
})();
